/**
 * Format a number as Kenyan shillings with `/-` suffix instead of full "KES".
 * e.g. 45600 → "45,600/-"
 */
export function fmtNum(n: number): string {
  return (
    new Intl.NumberFormat("en-KE", {
      style: "decimal",
      maximumFractionDigits: 0,
    }).format(n) + "/-"
  );
}

/**
 * Format a quantity — whole numbers show without decimals, fractional show with 1 decimal.
 */
export function fmtQty(n: number): string {
  return n % 1 === 0 ? n.toString() : n.toFixed(1);
}

/**
 * Open a new window and print the given HTML content.
 * Uses compact monospace layout with facility header.
 */
export function printHtml(title: string, bodyHtml: string) {
  const today = new Date().toLocaleDateString("en-KE", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${title}</title>
  <style>
    @page { margin: 8mm 6mm; }
    body {
      font-family: 'Courier New', Courier, monospace;
      font-size: 11px;
      margin: 0;
      padding: 0;
      background: white;
      color: black;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .print-wrap {
      max-width: 2480px;
      margin: 0 auto;
    }
    /* Facility header */
    .facility-header {
      text-align: center;
      border-bottom: 1px solid #999;
      padding-bottom: 4px;
      margin-bottom: 6px;
    }
    .facility-header .name {
      font-size: 14px;
      font-weight: bold;
      letter-spacing: 1px;
      text-transform: uppercase;
    }
    .facility-header .meta {
      font-size: 9px;
      color: #666;
      margin-top: 1px;
    }
    .facility-header .meta span {
      margin: 0 4px;
    }
    h1 {
      font-size: 12px;
      text-align: center;
      margin: 0 0 3px;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .subtitle {
      text-align: center;
      color: #666;
      font-size: 9px;
      margin-bottom: 6px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 11px;
    }
    th {
      background: #e5e7eb;
      font-weight: bold;
      text-align: left;
      padding: 3px 4px;
      border: 1px solid #d1d5db;
    }
    td {
      padding: 2px 4px;
      border: 1px solid #d1d5db;
      vertical-align: top;
    }
    td.num, th.num { text-align: right; }
    td.amount-cell { text-align: right; white-space: nowrap; }
    td .desc { text-align: left; }
    .total-row td {
      background: #f3f4f6;
      font-weight: bold;
      border-top: 2px solid #9ca3af;
      padding: 3px 4px;
    }
    .section-title {
      font-weight: bold;
      font-size: 12px;
      margin-top: 12px;
      margin-bottom: 4px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .summary-line {
      display: flex;
      justify-content: space-between;
      padding: 1px 0;
      font-size: 12px;
    }
    .summary-line.total {
      font-weight: bold;
      font-size: 13px;
      border-top: 1px solid #000;
      padding-top: 3px;
      margin-top: 3px;
    }
    .currency-header {
      text-align: right;
      font-size: 8px;
      color: #888;
      margin-bottom: 2px;
    }
    .footer {
      text-align: center;
      color: #888;
      font-size: 8px;
      margin-top: 12px;
    }
    @media print {
      body { margin: 0; padding: 6mm; }
    }
  </style>
</head>
<body>
  <div class="print-wrap">
    <div class="facility-header">
      <div class="name">White House Lounge</div>
      <div class="meta">
        <span>WHL-POS</span>
        <span>·</span>
        <span>${today}</span>
      </div>
    </div>
    <div class="currency-header">All amounts in KES</div>
    ${bodyHtml}
    <div class="footer">Printed ${new Date().toLocaleString()}</div>
  </div>
  <script>
    window.onload = function() { window.print(); window.close(); };
  </script>
</body>
</html>`;

  const win = window.open("", "_blank", "width=800,height=600");
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
}
